import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "./RichTextEditor";
import { Loader2 } from "lucide-react";

interface AddColumnModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddColumn: (columnName: string, enrichmentPrompt?: string, provider?: 'exa' | 'parallel' | 'anthropic') => void;
}

export function AddColumnModal({ open, onOpenChange, onAddColumn }: AddColumnModalProps) {
  const [columnName, setColumnName] = useState("");
  const [enrichmentPrompt, setEnrichmentPrompt] = useState("");
  const [enableEnrichment, setEnableEnrichment] = useState(false);
  const [provider, setProvider] = useState<'exa' | 'parallel' | 'anthropic'>('exa');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (columnName.trim() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await onAddColumn(
          columnName.trim(),
          enableEnrichment ? enrichmentPrompt.trim() : undefined,
          enableEnrichment ? provider : undefined
        );
        setColumnName("");
        setEnrichmentPrompt("");
        setEnableEnrichment(false);
        setProvider('exa');
        onOpenChange(false);
      } catch (error) {
        console.error("Failed to add column:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    setColumnName("");
    setEnrichmentPrompt("");
    setEnableEnrichment(false);
    setProvider('exa');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Column</DialogTitle>
          <DialogDescription>
            Create a new column and optionally enrich it with AI.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="columnName">Column Name</Label>
              <Input
                id="columnName"
                placeholder="e.g., Company Size, Industry, etc."
                value={columnName}
                onChange={(e) => setColumnName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enableEnrichment"
                  checked={enableEnrichment}
                  onChange={(e) => setEnableEnrichment(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="enableEnrichment" className="cursor-pointer">
                  Enrich with AI
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Automatically populate this column for all rows using AI research
              </p>
            </div>

            {enableEnrichment && (
              <>
                <div className="space-y-2">
                  <Label>Enrichment Provider</Label>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="provider-exa"
                        name="provider"
                        value="exa"
                        checked={provider === 'exa'}
                        onChange={(e) => setProvider(e.target.value as 'exa')}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="provider-exa" className="cursor-pointer font-normal">
                        Exa Research
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="provider-parallel"
                        name="provider"
                        value="parallel"
                        checked={provider === 'parallel'}
                        onChange={(e) => setProvider(e.target.value as 'parallel')}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="provider-parallel" className="cursor-pointer font-normal">
                        Parallel AI
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="provider-anthropic"
                        name="provider"
                        value="anthropic"
                        checked={provider === 'anthropic'}
                        onChange={(e) => setProvider(e.target.value as 'anthropic')}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="provider-anthropic" className="cursor-pointer font-normal">
                        Anthropic
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="enrichmentPrompt">Enrichment Prompt</Label>
                  <RichTextEditor
                    value={enrichmentPrompt}
                    onChange={setEnrichmentPrompt}
                    placeholder="Enter a prompt. Use {company_name} or {domain} to reference row data. Example: What is the industry of {company_name}?"
                    className="bg-background"
                  />
                  <p className="text-xs text-muted-foreground">
                    Available variables: {"{company_name}"}, {"{domain}"}, and any existing column names
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!columnName.trim() || (enableEnrichment && !enrichmentPrompt.trim()) || isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSubmitting
                ? "Processing..."
                : enableEnrichment
                  ? "Add & Enrich"
                  : "Add Column"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
